import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from "react";
import { AddApiKeySection } from "@/components/api-keys/AddApiKeySection";
import { ApiKeysGridSection } from "@/components/api-keys/ApiKeysGridSection";
import { ProductsDropdown } from "@/components/products/ProductsDropdown";
import { FILTER_VALUES } from "@/constants/filters";
import useAuthStore from "@/context/auth/useAuthStore";
import { useApiKeys, useCreateApiKey, useDeleteApiKey, useUpdateApiKeyStatus } from "@/hooks/api-keys";
import { useEffectiveCustomerId } from "@/hooks/useEffectiveCustomerId";
import { usePermissions } from "@/hooks/usePermissions";
import { useAvailableProducts } from "@/hooks/products";
import { DashboardPageFilters, DashboardPageLayout, DashboardPageLoading, DashboardPageSection } from "@/layouts/DashboardPageLayout";
import { filterMeteredProducts } from "@/utils/products";
export default function ApiKeysPage() {
    const { productCode } = useAuthStore();
    const { isViewer } = usePermissions();
    const { isSuperAdmin, selectedCustomerId, customerIdentifier } = useEffectiveCustomerId();
    const [selectedProduct, setSelectedProduct] = useState(FILTER_VALUES.ALL);
    const [statusActionError, setStatusActionError] = useState(null);
    // Hide CREATE button when viewing other org OR aggregate mode (all orgs)
    const isViewingOtherOrg = isSuperAdmin && selectedCustomerId !== undefined && (selectedCustomerId === null || // Aggregate mode (all orgs)
    selectedCustomerId !== customerIdentifier); // Different org
    const canMutate = !isViewer && !isViewingOtherOrg;
    // Fetch available products for the dropdown and create modal
    const { data: productsData, isLoading: productsLoading } = useAvailableProducts();
    const products = useMemo(()=>filterMeteredProducts(productsData?.products), [
        productsData?.products
    ]);
    // Fetch all API keys to discover products from expired contracts
    const { data: allApiKeysData } = useApiKeys("");
    // Derive products from expired contracts that still have API keys
    const expiredProducts = useMemo(()=>{
        if (!allApiKeysData?.items) return [];
        const activeProductCodes = new Set(productsData?.products?.map((p)=>p.productCode) ?? []);
        // Collect unique product codes from keys that aren't in active products
        const expiredMap = new Map();
        for (const key of allApiKeysData.items){
            if (key.productCode && !activeProductCodes.has(key.productCode)) {
                expiredMap.set(key.productCode, key.productCode);
            }
        }
        return Array.from(expiredMap, ([productCode, productName])=>({
                productCode,
                productName
            }));
    }, [
        allApiKeysData?.items,
        productsData?.products
    ]);
    // Don't render if we don't have a productCode yet
    if (!productCode) {
        return /*#__PURE__*/ _jsx(DashboardPageLayout, {
            title: "API Keys",
            description: "Manage your API keys for accessing AWS Portal services",
            children: /*#__PURE__*/ _jsx(DashboardPageLoading, {})
        });
    }
    const activeProductCode = selectedProduct === FILTER_VALUES.ALL ? "" : selectedProduct;
    const { data: apiKeysData, isLoading, error, refetch } = useApiKeys(activeProductCode);
    // Initialize mutations
    const createApiKeyMutation = useCreateApiKey();
    const deleteApiKeyMutation = useDeleteApiKey();
    const updateApiKeyStatusMutation = useUpdateApiKeyStatus();
    const handleCreateApiKey = async (data, productCodeForKey)=>{
        try {
            const result = await createApiKeyMutation.mutateAsync({
                data,
                productCodeForKey
            });
            return result;
        } catch (error) {
            throw error;
        }
    };
    const handleDeleteApiKey = async (keyId, productCode)=>{
        try {
            await deleteApiKeyMutation.mutateAsync({
                keyId,
                productCode
            });
        } catch (error) {
            throw error;
        }
    };
    const handleUpdateApiKeyStatus = async (keyId, productCode, action)=>{
        try {
            setStatusActionError(null);
            await updateApiKeyStatusMutation.mutateAsync({
                keyId,
                productCode,
                action
            });
        } catch (error) {
            const fallbackMessage = `Failed to ${action} API key`;
            const errorMessage = error instanceof Error ? error.message : fallbackMessage;
            setStatusActionError(errorMessage);
        }
    };
    if (isLoading) {
        return /*#__PURE__*/ _jsx(DashboardPageLayout, {
            title: "API Keys",
            description: "Manage your API keys for accessing AWS Portal services",
            children: /*#__PURE__*/ _jsx(DashboardPageLoading, {})
        });
    }
    if (error) {
        return /*#__PURE__*/ _jsx(DashboardPageLayout, {
            title: "API Keys",
            description: "Manage your API keys for accessing AWS Portal services",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "text-red-600",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-xl font-bold mb-2",
                        children: "Error"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: error.message || "There was an error loading your API keys."
                    })
                ]
            })
        });
    }
    const createApiKeyAction = canMutate ? /*#__PURE__*/ _jsx(AddApiKeySection, {
        onCreateApiKey: handleCreateApiKey,
        onModalClose: ()=>{
            // Always refetch the data when modal closes to ensure we have latest data
            refetch();
        },
        isLoading: createApiKeyMutation.isPending,
        products: products,
        defaultProductCode: activeProductCode,
        productsLoading: productsLoading
    }) : null;
    return /*#__PURE__*/ _jsxs(DashboardPageLayout, {
        title: "API Keys",
        description: "Manage your API keys for accessing AWS Portal services",
        actions: createApiKeyAction,
        children: [
            /*#__PURE__*/ _jsx(DashboardPageFilters, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "w-full max-w-xs",
                    children: [
                        /*#__PURE__*/ _jsx(ProductsDropdown, {
                            value: selectedProduct,
                            onChange: setSelectedProduct,
                            extraProducts: expiredProducts
                        }),
                        statusActionError && /*#__PURE__*/ _jsx("p", {
                            className: "mt-2 text-sm text-red-600",
                            children: statusActionError
                        }),
                        selectedProduct !== FILTER_VALUES.ALL && (()=>{
                            const product = products.find((p)=>p.productCode === selectedProduct);
                            if (!product?.usagePlan) return null;
                            const available = product.usagePlan.availableApiKeySlots;
                            return /*#__PURE__*/ _jsx("p", {
                                className: `mt-2 text-sm ${available > 0 ? "text-gray-600" : "text-red-600"}`,
                                children: available > 0 ? `${available} API key slot${available !== 1 ? "s" : ""} available` : "No API key slots available"
                            });
                        })()
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(DashboardPageSection, {
                className: "h-[calc(100vh-18rem)] min-h-[400px]",
                children: /*#__PURE__*/ _jsx(ApiKeysGridSection, {
                    emptyAction: canMutate ? /*#__PURE__*/ _jsx(AddApiKeySection, {
                        onCreateApiKey: handleCreateApiKey,
                        onModalClose: ()=>{
                            refetch();
                        },
                        isLoading: createApiKeyMutation.isPending,
                        products: products,
                        defaultProductCode: activeProductCode,
                        productsLoading: productsLoading
                    }) : undefined,
                    emptyMessage: canMutate ? "Create an API key when this account is ready to send entropy requests." : undefined,
                    emptyTitle: canMutate ? "Create your first API key" : undefined,
                    rows: (apiKeysData?.items || []).map((item)=>{
                        // Check if contract is expired for this product
                        // If product is not in availableProducts, contract is expired (availableProducts only returns active contracts)
                        const product = productsData?.products?.find((p)=>p.productCode === item.productCode);
                        const contractExpired = !product;
                        return {
                            ...item,
                            contractExpired
                        };
                    }),
                    onDelete: handleDeleteApiKey,
                    onStatusUpdate: handleUpdateApiKeyStatus,
                    heightMode: "fill",
                    isLoading: isLoading,
                    deleteLoading: deleteApiKeyMutation.isPending,
                    statusUpdateLoading: updateApiKeyStatusMutation.isPending ? updateApiKeyStatusMutation.variables?.keyId ?? null : null,
                    isViewer: isViewer,
                    userCustomerId: customerIdentifier
                })
            })
        ]
    });
}
